'use client';

import { useEffect, useRef } from 'react';

/**
 * Global tooltip manager.
 *
 * Ascultă la mouseover/focus pe elementele cu atributul `data-tooltip` SAU
 * atributul nativ `title`, și afișează un tooltip instant, poziționat
 * inteligent (auto-flip top/bottom). Pentru elementele cu `title`, atributul
 * nativ e mutat temporar în `data-native-title` cât timp user-ul e în hover,
 * apoi restaurat — astfel se evită tooltip-ul dublu al browserului.
 *
 * Montat o singură dată în layout-ul aplicației.
 */
export function GlobalTooltip() {
  const tipRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const tip = document.createElement('div');
    tip.className = 'sf-tooltip';
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
    tipRef.current = tip;

    function getTooltipText(el: HTMLElement): string | null {
      const dt = el.getAttribute('data-tooltip');
      if (dt && dt.trim()) return dt;
      const title = el.getAttribute('title');
      if (title && title.trim()) return title;
      const nt = el.getAttribute('data-native-title');
      if (nt && nt.trim()) return nt;
      return null;
    }

    function suppressNativeTitle(el: HTMLElement) {
      const title = el.getAttribute('title');
      if (title) {
        el.setAttribute('data-native-title', title);
        el.removeAttribute('title');
      }
    }

    function restoreNativeTitle(el: HTMLElement) {
      const nt = el.getAttribute('data-native-title');
      if (nt) {
        el.setAttribute('title', nt);
        el.removeAttribute('data-native-title');
      }
    }

    function positionTooltip(target: HTMLElement) {
      if (!tipRef.current) return;
      const t = tipRef.current;
      const rect = target.getBoundingClientRect();
      const tipRect = t.getBoundingClientRect();
      const vw = window.innerWidth;

      let top = rect.top - tipRect.height - 8;
      let side: 'top' | 'bottom' = 'top';
      if (top < 8) {
        top = rect.bottom + 8;
        side = 'bottom';
      }
      let left = rect.left + rect.width / 2 - tipRect.width / 2;
      left = Math.max(6, Math.min(left, vw - tipRect.width - 6));

      // position: fixed -> coordonate de viewport direct (fara scroll offset).
      // Aplicatia scrolleaza prin <main overflow-y-auto>, nu prin window, deci
      // window.scrollY e ~0; `fixed` + getBoundingClientRect e corect pe orice scroll.
      t.style.top = top + 'px';
      t.style.left = left + 'px';
      t.dataset.side = side;
    }

    function show(target: HTMLElement) {
      if (!tipRef.current) return;
      if (activeRef.current === target) return;
      if (activeRef.current) restoreNativeTitle(activeRef.current);
      activeRef.current = target;
      const text = getTooltipText(target);
      if (!text) return;
      suppressNativeTitle(target);
      const t = tipRef.current;
      t.textContent = text;
      t.classList.remove('show');
      t.style.top = '-9999px';
      t.style.left = '-9999px';
      requestAnimationFrame(() => {
        if (activeRef.current !== target) return;
        positionTooltip(target);
        t.classList.add('show');
      });
    }

    function hide() {
      if (activeRef.current) restoreNativeTitle(activeRef.current);
      activeRef.current = null;
      tipRef.current?.classList.remove('show');
    }

    function resolveTarget(node: EventTarget | null): HTMLElement | null {
      if (!node || !(node as Element).closest) return null;
      const el =
        (node as Element).closest('[data-tooltip]') ||
        (node as Element).closest('[title]') ||
        (node as Element).closest('[data-native-title]');
      return (el as HTMLElement) || null;
    }

    function onOver(e: MouseEvent) {
      const el = resolveTarget(e.target);
      if (el) show(el);
    }
    function onOut(e: MouseEvent) {
      const el = resolveTarget(e.target);
      if (el && !el.contains(e.relatedTarget as Node | null)) hide();
    }
    function onFocusIn(e: FocusEvent) {
      const el = resolveTarget(e.target);
      if (el) show(el);
    }
    function onFocusOut(e: FocusEvent) {
      const el = resolveTarget(e.target);
      if (el) hide();
    }
    function onScroll() {
      if (activeRef.current) positionTooltip(activeRef.current);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') hide();
    }
    function onClick() {
      // hide on click so it doesn't linger after an action
      hide();
    }

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);

    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      if (activeRef.current) restoreNativeTitle(activeRef.current);
      tip.remove();
      tipRef.current = null;
    };
  }, []);

  return null;
}
